/**
 * Product Form Page
 *
 * Implements: US-M5-002 (Manage Products CRUD)
 * AC-158: Add product form includes all required fields
 * AC-159: Multiple images can be uploaded (max 10)
 * AC-160: Colour variants can be added with separate images
 * AC-161: Pricing tiers (20, 50, 100, 100+) are configurable
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Spinner,
  Banner,
  Box,
  Modal,
} from '@shopify/polaris';
import { productService, type CreateProductInput } from '../../services/product.service';
import { categoryService, type Category } from '../../services/category.service';
import { CloudinaryUpload } from '../../components/common/CloudinaryUpload';

// Default pricing tiers as per PRD
const DEFAULT_PRICING_TIERS = [
  { quantityTier: '20', unitPrice: 0 },
  { quantityTier: '50', unitPrice: 0 },
  { quantityTier: '100', unitPrice: 0 },
  { quantityTier: '100+', unitPrice: 0 },
];

export function ProductFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id && id !== 'new';

  // Form state
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [sku, setSku] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [samplePrice, setSamplePrice] = useState('');
  const [isHotSelling, setIsHotSelling] = useState(false);
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [pricing, setPricing] = useState(DEFAULT_PRICING_TIERS);

  // Images state
  const [images, setImages] = useState<Array<{
    id?: string;
    imageUrl: string;
    isPrimary: boolean;
    colorVariant: string;
    displayOrder: number;
  }>>([]);

  // UI state
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteImageModalOpen, setDeleteImageModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);

  // Load categories
  useEffect(() => {
    categoryService.list().then(setCategories).catch(console.error);
  }, []);

  // Load product if editing
  useEffect(() => {
    if (isEditing && id) {
      setLoading(true);
      productService
        .getById(id)
        .then((product) => {
          setName(product.name);
          setSlug(product.slug);
          setSku(product.sku);
          setDescription(product.description || '');
          setCategoryId(product.categoryId);
          setSamplePrice(product.samplePrice?.toString() || '');
          setIsHotSelling(product.isHotSelling);
          setStatus(product.status);
          setImages(
            product.images.map((img) => ({
              id: img.id,
              imageUrl: img.imageUrl,
              isPrimary: img.isPrimary,
              colorVariant: img.colorVariant || '',
              displayOrder: img.displayOrder,
            }))
          );
          if (product.pricing.length > 0) {
            setPricing(
              product.pricing.map((p) => ({
                quantityTier: p.quantityTier,
                unitPrice: p.unitPrice,
              }))
            );
          }
        })
        .catch((err) => {
          console.error('Failed to load product:', err);
          setError('Failed to load product');
        })
        .finally(() => setLoading(false));
    }
  }, [isEditing, id]);

  // Auto-generate slug from name
  const handleNameChange = useCallback((value: string) => {
    setName(value);
    if (!isEditing) {
      const newSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setSlug(newSlug);
    }
  }, [isEditing]);

  // Handle pricing change
  const handlePricingChange = useCallback((index: number, value: string) => {
    setPricing((prev) =>
      prev.map((p, i) =>
        i === index ? { ...p, unitPrice: parseFloat(value) || 0 } : p
      )
    );
  }, []);

  // Handle image removal (for existing images with IDs)
  const handleImageRemove = useCallback((imageId: string) => {
    setImageToDelete(imageId);
    setDeleteImageModalOpen(true);
  }, []);

  const confirmDeleteImage = useCallback(async () => {
    if (!imageToDelete || !id) return;
    try {
      await productService.deleteImage(id, imageToDelete);
      setImages((prev) => prev.filter((img) => img.id !== imageToDelete));
    } catch (err) {
      console.error('Failed to delete image:', err);
    } finally {
      setDeleteImageModalOpen(false);
      setImageToDelete(null);
    }
  }, [imageToDelete, id]);

  // Validate form
  const validateForm = useCallback((): string | null => {
    if (!name.trim()) return 'Name is required';
    if (!slug.trim()) return 'Slug is required';
    if (!sku.trim()) return 'SKU is required';
    if (!categoryId) return 'Category is required';
    if (!/^[a-z0-9-]+$/.test(slug)) return 'Slug can only contain lowercase letters, numbers, and hyphens';
    return null;
  }, [name, slug, sku, categoryId]);

  // Submit form
  const handleSubmit = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const productData: CreateProductInput = {
        name: name.trim(),
        slug: slug.trim(),
        sku: sku.trim(),
        description: description.trim() || undefined,
        categoryId,
        samplePrice: samplePrice ? parseFloat(samplePrice) : undefined,
        isHotSelling,
        status,
        pricing: pricing.filter((p) => p.unitPrice > 0),
      };

      if (isEditing && id) {
        await productService.update(id, productData);
        // Handle new images (those without IDs are newly uploaded)
        const newImages = images.filter((img) => !img.id);
        if (newImages.length > 0) {
          await productService.addImages(id, newImages);
        }
      } else {
        productData.images = images;
        await productService.create(productData);
      }

      navigate('/products');
    } catch (err: any) {
      console.error('Failed to save product:', err);
      setError(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  }, [
    validateForm,
    name,
    slug,
    sku,
    description,
    categoryId,
    samplePrice,
    isHotSelling,
    status,
    pricing,
    images,
    isEditing,
    id,
    navigate,
  ]);

  // Category options
  const categoryOptions = [
    { label: 'Select a category', value: '' },
    ...categories.map((cat) => ({ label: cat.name, value: cat.id })),
  ];

  // Status options
  const statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' },
  ];

  if (loading) {
    return (
      <Page title="Loading...">
        <Card>
          <Box padding="600">
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Box>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title={isEditing ? 'Edit Product' : 'Add Product'}
      backAction={{ content: 'Products', onAction: () => navigate('/products') }}
      primaryAction={{
        content: 'Save',
        loading: saving,
        onAction: handleSubmit,
      }}
    >
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        )}

        {/* Basic Information */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Basic Information
            </Text>
            <FormLayout>
              <TextField
                label="Product Name"
                value={name}
                onChange={handleNameChange}
                autoComplete="off"
                requiredIndicator
              />
              <FormLayout.Group>
                <TextField
                  label="Slug"
                  value={slug}
                  onChange={setSlug}
                  autoComplete="off"
                  helpText="URL-friendly identifier (lowercase, numbers, hyphens only)"
                  requiredIndicator
                />
                <TextField
                  label="SKU"
                  value={sku}
                  onChange={setSku}
                  autoComplete="off"
                  requiredIndicator
                />
              </FormLayout.Group>
              <TextField
                label="Description"
                value={description}
                onChange={setDescription}
                multiline={4}
                autoComplete="off"
              />
              <FormLayout.Group>
                <Select
                  label="Category"
                  options={categoryOptions}
                  value={categoryId}
                  onChange={setCategoryId}
                  requiredIndicator
                />
                <Select
                  label="Status"
                  options={statusOptions}
                  value={status}
                  onChange={(value) => setStatus(value as 'ACTIVE' | 'INACTIVE')}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField
                  label="Sample Price"
                  type="number"
                  value={samplePrice}
                  onChange={setSamplePrice}
                  prefix="₹"
                  autoComplete="off"
                />
                <Box paddingBlockStart="600">
                  <Checkbox
                    label="Mark as Hot Selling"
                    checked={isHotSelling}
                    onChange={setIsHotSelling}
                  />
                </Box>
              </FormLayout.Group>
            </FormLayout>
          </BlockStack>
        </Card>

        {/* Pricing Tiers */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Quantity Pricing Tiers
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Set unit prices for different quantity tiers. Leave at 0 to not offer that tier.
            </Text>
            <FormLayout>
              <FormLayout.Group condensed>
                {pricing.map((tier, index) => (
                  <TextField
                    key={tier.quantityTier}
                    label={`${tier.quantityTier} pcs`}
                    type="number"
                    value={tier.unitPrice.toString()}
                    onChange={(value) => handlePricingChange(index, value)}
                    prefix="₹"
                    autoComplete="off"
                  />
                ))}
              </FormLayout.Group>
            </FormLayout>
          </BlockStack>
        </Card>

        {/* Images */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Product Images
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Upload up to 10 images. You can specify color variants for each image.
            </Text>

            <CloudinaryUpload
              images={images}
              onImagesChange={setImages}
              maxImages={10}
              folder="gifting-products"
              onImageRemove={handleImageRemove}
            />
          </BlockStack>
        </Card>

        {/* Actions */}
        <InlineStack gap="300" align="end">
          <Button onClick={() => navigate('/products')}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>
            {isEditing ? 'Save Changes' : 'Create Product'}
          </Button>
        </InlineStack>
      </BlockStack>

      {/* Delete Image Modal */}
      <Modal
        open={deleteImageModalOpen}
        onClose={() => setDeleteImageModalOpen(false)}
        title="Delete image?"
        primaryAction={{
          content: 'Delete',
          destructive: true,
          onAction: confirmDeleteImage,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setDeleteImageModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <Text as="p">Are you sure you want to delete this image?</Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
